<template>
  <div class="limit-change">
    <h2 class="title">{{ $t('limitChange.title') }}</h2>
    <transition mode="out-in" name="fade">
      <loader-with-message v-if="limitLoading"
                           :has-error="limitLoadError"
                           @retry="fetchCurrentLimit"
                           class="roaming-loader"/>
      <div v-else>
        <div class="current-limit">
          <span class="current-limit-title">{{ $t('limitChange.currentLimit') }}</span>
          <span class="current-limit-value">{{ currentLimit }}</span>
        </div>
        <p class="change-limit-text">{{ $t('limitChange.youCanChangeTheLimit') }}</p>
        <validation-observer ref="limitChange"
                             tag="form"
                             @submit.prevent="changeLimit">
          <base-v-input v-model.trim="setLimit"
                        variant="white"
                        :label="$t('limitChange.enterNewLimit')"
                        :placeholder="limitRange"
                        :submitted="limitSubmitted"
                        :rules="`required|currencyRange:${minLimit},${maxLimit}|max:3`"
                        maxlength="3"
                        @input="onlyWholeNumbers"/>
          <base-button type="submit">{{ $t('common.change') }}</base-button>
        </validation-observer>
      </div>
    </transition>
  </div>
</template>

<script>
import LoaderWithMessage from '@/components/common/loader-with-message'
import selectionBlockEvents from '@/mixins/selectionBlockEvents'
import errorMessageHandler from '@/mixins/errorMessageHandler'

export default {
  name: 'limit-change',
  components: { LoaderWithMessage },
  mixins: [selectionBlockEvents, errorMessageHandler],
  props: {
    resourcesData: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      currentLimit: 0,
      limitRange: 0,
      minLimit: 0,
      maxLimit: 0,
      setLimit: null,
      limitLoading: true,
      limitLoadError: false,
      limitSubmitted: false
    }
  },

  methods: {
    fetchCurrentLimit () {
      const subscriberIdentity = this.$props.resourcesData.subscriberIdentity
      if (subscriberIdentity) {
        this.limitLoading = true
        this.limitLoadError = false
        this.$apiRequest.get(`user/services/limit?subscriberIdentity=${subscriberIdentity}`)
          .then(response => {
            this.currentLimit = response.data.formattedCurrentLimit
            this.limitRange = response.data.formattedRangeLimit
            this.minLimit = response.data.minLimit
            this.maxLimit = response.data.maxLimit
            this.limitLoading = false
            this.limitLoadError = false
          })
          .catch(() => {
            this.limitLoadError = true
          })
          .finally(() => {
            this.$nextTick(() => {
              this.unblockSidebarSelection(false)
            })
          })
      }
    },
    async changeLimit () {
      const isValid = await this.$refs.limitChange.validate()
      this.limitSubmitted = true
      if (isValid) {
        this.$loader.show()
        const dataToSend = {
          subscriberIdentity: this.$props.resourcesData.subscriberIdentity,
          limitAmount: this.setLimit
        }
        this.$apiRequest.post('user/services/limit', dataToSend)
          .then(response => {
            if (response.status >= 200 && response.status < 400) {
              this.toast('success', {
                title: this.$t('limitChange.changedSuccessfully')
              })
              this.fetchCurrentLimit()
              this.setLimit = null
              this.limitSubmitted = false
            }
          })
          .catch(e => {
            this.handleErrorMessage(e.response.data)
          })
          .finally(() => {
            this.$loader.hide()
          })
      }
    },
    onlyWholeNumbers () {
      const regex = /[.,\s]/g
      this.setLimit = this.setLimit.replace(regex, '')
    }
  },
  mounted () {
    this.fetchCurrentLimit()
  }
}
</script>

<style lang="scss" scoped>
.limit-change {
  color: $text;
  padding-top: 2rem;

  .title {
    margin-bottom: 2.4rem;
    font-size: 2.4rem;
    font-weight: normal;
    text-transform: uppercase;
    padding-bottom: .8rem;
    border-bottom: .1rem solid $form-border-color;
  }
}

.current-limit {
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 .5rem 1.5rem 0 rgba(0, 0, 0, .08);
  border-radius: .5rem;
  margin-bottom: 2.4rem;
  padding: 1.8rem 1.6rem 1.6rem 2.4rem;
  background: $white;
  font-weight: 600;
}

.current-limit-value {
  color: $mtel-red;
  font-size: 2.4rem;
}

.change-limit-text {
  margin-bottom: 1.6rem;
}
</style>
